/**
 * First-time voice-scoring tutorial.
 *
 * Auto-opens once per browser when:
 *   - the Web Speech API is supported (no point teaching what won't work)
 *   - the user has never dismissed it (localStorage flag
 *     `zcd_voice_tour_seen`)
 *
 * Founder ask 2026-05-26: "prompt users on how to use the voice scoring
 * feature." The full reference cheat-sheets (VoiceCommandsHelp,
 * SpatialVoiceHelp) live elsewhere on the page; this is the 60-second
 * walk-through that gets a new scorer from "what does that mic do?" to
 * "I can score with my voice now." Five short steps, prev/next, and a
 * single "Got it — start scoring" close button.
 *
 * Re-trigger: VoiceScoringPanel exposes a small "Tour" link that calls
 * onReplay() so an operator can pull up the walk-through again from any
 * BBP session.
 */
import { useEffect, useRef, useState } from "react";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import {
  Mic, Sparkles, Crosshair, MessageSquare, Languages,
  ChevronLeft, ChevronRight, X, ListOrdered,
} from "lucide-react";
import VoicePlayButton from "./VoicePlayButton";


const STEPS = [
  {
    icon: ListOrdered,
    title: "Recommended scoring order (not enforced)",
    body: (
      <>
        Most scorers find this flow natural per ball. <strong>Not
        mandatory</strong> — voice any field in any order, and you can
        always come back to edit a saved ball later via the ball-list
        / Edit Ball dialog. Bowler + Batsman usually carry over from
        the previous ball — only voice them when they change.
        <ol className="list-decimal list-inside mt-2 text-[11px] text-text-secondary space-y-0.5">
          <li>Clip in (start of ball video)</li>
          <li>Bowler — only on new over</li>
          <li>Batsman — only on wicket / strike change</li>
          <li>Pitch map (where the ball pitched)</li>
          <li>Height impact (where it met bat / pad)</li>
          <li>Wagon wheel (where it ended up — skip on bowled / LBW / leg-bye / padded)</li>
          <li>Performance metrics (delivery type, stroke, foot movement…)</li>
          <li>Scores + actions (runs, extras, wicket)</li>
          <li>Clip out (end of ball video)</li>
          <li>Save ball</li>
        </ol>
      </>
    ),
    examples: ["clip in", "next bowler bumrah", "kohli on strike", "pitch yorker outside off"],
    note: "Edit later anytime — saved balls open in the Edit Ball dialog from the recent-balls strip.",
  },
  {
    icon: Mic,
    title: "Press V to start the mic",
    body: (
      <>
        Tap the green <strong>Voice</strong> button in the top bar, or
        just press the <kbd className="px-1.5 py-0.5 rounded bg-bg-secondary border border-border-default text-[10px] font-mono">V</kbd>
        {" "}key while you're not typing. The button turns red and pulses
        when it's listening. Press <kbd className="px-1.5 py-0.5 rounded bg-bg-secondary border border-border-default text-[10px] font-mono">V</kbd> again to stop.
      </>
    ),
    examples: ["press V", "tap the mic"],
  },
  {
    icon: MessageSquare,
    title: "Say what happened on the ball",
    body: (
      <>
        Speak naturally — runs, extras, dismissals, all in one phrase.
        Compounds work: <em>"four boundary"</em>, <em>"wide one"</em>,
        {" "}<em>"caught Bumrah"</em>. Say <em>"save"</em> when you're
        ready to record the ball, or <em>"undo"</em> to revert the last
        one.
      </>
    ),
    examples: ["dot", "single", "four boundary", "wide one", "caught bowled", "save", "undo"],
  },
  {
    icon: Crosshair,
    title: "Lock the scope to avoid cross-matches",
    body: (
      <>
        If a word fits more than one metric (e.g. <em>"square"</em>),
        prefix the phrase with the field name. <em>"stroke square cut"</em>
        {" "}only sets Stroke. <em>"pitch yorker"</em> only sets the
        pitch zone. Available scopes: <em>stroke</em>, <em>delivery</em>,
        {" "}<em>spin</em>, <em>pitch</em>, <em>impact</em>, <em>wagon</em>,
        {" "}<em>keeper</em>, <em>bounce</em>, <em>connection</em>,
        {" "}<em>foot</em>, <em>air</em>, <em>control</em>, <em>commentary</em>.
      </>
    ),
    examples: ["stroke cover drive", "pitch yorker outside off", "wagon deep cover", "impact chest middle stump"],
  },
  {
    icon: Crosshair,
    title: "Jump straight to a matrix",
    body: (
      <>
        Say a scope name <strong>by itself</strong> to switch matrices
        without setting a value — handy when you want to mark a pitch /
        impact / wagon position with the touchscreen after speaking the
        rest of the ball. Say <em>"wicket"</em> on its own to pop open
        the dismissal-type picker.
      </>
    ),
    examples: ["height impact", "wagon wheel", "pitch", "wicket", "out"],
  },
  {
    icon: Sparkles,
    title: "Turn on ✨ AI for natural phrases",
    body: (
      <>
        When the regex parser can't make sense of what you said, the
        ✨ <strong>AI</strong> toggle (next to the mic) sends the
        transcript to Claude Haiku, which proposes structured updates.
        Each fallback call costs <strong>1 ZC</strong> and shows up as
        a <em>"AI suggests…"</em> banner with a 2-second timer — tap
        <strong> ✓ Apply</strong> to commit immediately or
        <strong> ✕ Cancel</strong> to drop it. Hindi mixes work too:
        {" "}<em>chauka</em> (four), <em>chakka</em> (six),
        {" "}<em>vikit</em> (wicket).
      </>
    ),
    examples: ["he edged it past the keeper for four", "chauka over cover", "vikit lbw"],
    note: "AI fallback is opt-in. The regex parser handles ~95% of common phrases for free.",
  },
  {
    icon: Languages,
    title: "Hindi / Hinglish cues (with AI on)",
    body: (
      <>
        With the ✨ AI toggle on, mixed-language phrases parse fine:
        {" "}<em>chauka</em> → 4 + boundary, <em>chakka</em> → 6,
        {" "}<em>vikit</em> → wicket, <em>aage badhke</em> → down the
        track, <em>pichhe hatke</em> → backs away,
        {" "}<em>off ka stump pe</em> → off-stump line. Filler words
        like <em>achha</em> / <em>shaandar</em> are ignored unless
        you're dictating commentary.
      </>
    ),
    examples: ["chauka cover se", "vikit, bowled", "aage badhke six", "middle stump pe full"],
  },
];


const STORAGE_KEY = "zcd_voice_tour_seen";


export default function VoiceTutorialModal({ open, onClose }) {
  const [step, setStep] = useState(0);
  const panelRef = useRef(null);
  // Wraps the existing Esc-handler stub — now also closes on Android
  // hardware-back + traps focus + locks scroll for the duration.
  useDialogDismiss({
    open,
    onClose: () => {
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* private mode */ }
      onClose?.();
    },
    ref: panelRef,
  });

  // Reset to step 1 each time the modal re-opens so a replay always
  // starts from the top. Without this, dismissing on step 5 and
  // re-opening jumps straight to step 5.
  useEffect(() => { if (open) setStep(0); }, [open]);

  if (!open) return null;

  const total = STEPS.length;
  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === total - 1;

  const handleClose = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* private mode */ }
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-tour-title"
    >
      <div
        ref={panelRef}
        className="bg-bg-primary rounded-2xl max-w-lg w-full border border-purple-400/40 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-bg-secondary bg-gradient-to-r from-purple-500/10 to-transparent">
          <div className="flex items-center gap-2">
            <Mic className="w-4 h-4 text-purple-300" />
            <h3 id="voice-tour-title" className="text-sm font-semibold uppercase tracking-wider text-purple-200">
              Voice scoring — quick tour
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-text-secondary hover:text-text-primary"
            aria-label="Close tutorial"
            title="Skip tutorial"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="shrink-0 w-9 h-9 rounded-full bg-purple-500/20 border border-purple-400/40 flex items-center justify-center">
              <Icon className="w-4 h-4 text-purple-300" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-text-secondary">
                Step {step + 1} of {total}
              </div>
              <h4 className="text-base font-semibold text-text-primary leading-tight">
                {s.title}
              </h4>
            </div>
          </div>

          <p className="text-sm text-text-secondary leading-relaxed">{s.body}</p>

          {s.examples?.length ? (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-purple-300 font-medium mb-1 flex items-center gap-1.5">
                Try saying <span className="text-text-secondary normal-case tracking-normal">— tap the speaker to hear it</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {s.examples.map((ex) => (
                  <span
                    key={ex}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-bg-secondary/80 border border-purple-400/20 rounded text-[11px] font-mono text-text-primary"
                  >
                    "{ex}"
                    <VoicePlayButton phrase={ex} size="xs" />
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {s.note ? (
            <p className="mt-3 text-[11px] text-text-secondary italic border-l-2 border-purple-400/50 pl-2">
              {s.note}
            </p>
          ) : null}
        </div>

        <div className="px-5 py-3 border-t border-bg-secondary flex items-center justify-between gap-2 bg-bg-secondary/30">
          <div className="flex items-center gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`block w-1.5 h-1.5 rounded-full transition-colors ${
                  i === step ? "bg-purple-400" : "bg-bg-secondary border border-border-default"
                }`}
                aria-hidden
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setStep((n) => Math.max(0, n - 1))}
              disabled={step === 0}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-text-secondary hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Back
            </button>
            {isLast ? (
              <button
                onClick={handleClose}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-purple-500/30 border border-purple-400/60 text-purple-100 text-xs font-semibold uppercase tracking-wider hover:bg-purple-500/40"
              >
                Got it — start scoring
              </button>
            ) : (
              <button
                onClick={() => setStep((n) => Math.min(total - 1, n + 1))}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-purple-500/20 border border-purple-400/50 text-purple-200 text-xs font-semibold uppercase tracking-wider hover:bg-purple-500/30"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


export function shouldShowVoiceTour() {
  try { return localStorage.getItem(STORAGE_KEY) !== "1"; }
  catch { return false; }
}

export function clearVoiceTourSeen() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
